import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { ConfirmDialog } from '@codespeak11/bdance-theme';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import IconButton from '@mui/material/IconButton';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PersonaSelector from '../../components/PersonaSelector';
import ImportProjectDialog from '../../components/ImportProjectDialog';
import PublishToLibraryDialog from '../../components/PublishToLibraryDialog';
import { getUserSub } from '../../services/tokenResolver';
import { personaWizApi, PersonaSummary, projectsApi } from '../../services/api';

interface ProjectListItem {
  entityId: string;
  projectName: string;
  description: string;
  bookId?: string;
  packCount: number;
  promptCount: number;
  modifiedDate?: string;
}

interface ProjectPrompt {
  entityId: string;
  promptName: string;
  description: string;
  userPrompt: string;
  generatedPrompt: string;
}

interface ProjectPack {
  entityId: string;
  promptPackName: string;
  description: string;
  chapterIndex?: number;
  chapterTitle?: string;
  prompts: ProjectPrompt[];
}

interface ProjectDetail {
  project: {
    entityId: string;
    projectName: string;
    description: string;
    bookId?: string;
    bookTitle?: string;
    bookAuthor?: string;
  };
  packs: ProjectPack[];
}

interface OptimizeBatchResult {
  promptId: string;
  promptName: string;
  status: 'staged' | 'committed' | 'cleared' | 'skipped' | 'failed';
  reason?: string;
}

interface OptimizeProgressState {
  phase: 'idle' | 'staging' | 'committing' | 'clearing' | 'done';
  totalPromptCount: number;
  stageProcessed: number;
  commitProcessed: number;
  clearProcessed: number;
  stagedCount: number;
  committedCount: number;
  clearedCount: number;
  skippedCount: number;
  stagedReadyCount: number;
  unstagedCount: number;
}

function exportProjectPromptPackPdf(detail: ProjectDetail): void {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 54;
  const topMargin = 56;
  const bottomMargin = 56;
  const contentWidth = pageWidth - marginX * 2;
  const lineHeight = 15;
  const accentColor: [number, number, number] = [18, 64, 103];
  const textColor: [number, number, number] = [20, 20, 20];
  const mutedColor: [number, number, number] = [106, 115, 125];

  const truncateToWidth = (value: string, width: number): string => {
    if (doc.getTextWidth(value) <= width) return value;

    let result = value;
    while (result.length > 1 && doc.getTextWidth(`${result}...`) > width) {
      result = result.slice(0, -1);
    }

    return `${result}...`;
  };

  const orderedPacks = [...detail.packs].sort((a, b) => (a.chapterIndex ?? 999) - (b.chapterIndex ?? 999));

  const chapterRows = orderedPacks.map((pack, packIndex) => {
    const chapterNumber = pack.chapterIndex ?? packIndex + 1;
    const chapterTitle = pack.chapterTitle || pack.promptPackName || `Chapter ${chapterNumber}`;

    const prompts = pack.prompts
      .map((prompt) => {
      const promptTitle =
        pack.chapterTitle && prompt.promptName.startsWith(`${pack.chapterTitle}: `)
          ? prompt.promptName.slice(pack.chapterTitle.length + 2)
          : prompt.promptName;

      const promptText = prompt.generatedPrompt || prompt.userPrompt || '';
      const deepLink = `https://promptfab.ai/optimizer?promptId=${encodeURIComponent(prompt.entityId)}&projectId=${encodeURIComponent(detail.project.entityId)}`;

      return {
        promptId: prompt.entityId,
        promptTitle,
        promptText,
        deepLink,
      };
      })
      .filter((prompt) => prompt.promptText.trim().length > 0);

    return {
      chapterNumber,
      chapterTitle,
      prompts,
    };
  }).filter((chapter) => chapter.prompts.length > 0);

  const tocRows: Array<
    | { type: 'chapter'; text: string }
    | { type: 'prompt'; text: string; promptId: string }
  > = [];

  chapterRows.forEach((chapter) => {
    tocRows.push({
      type: 'chapter',
      text: `Chapter ${chapter.chapterNumber}: ${chapter.chapterTitle}`,
    });
    chapter.prompts.forEach((prompt, promptIndex) => {
      tocRows.push({
        type: 'prompt',
        text: `${promptIndex + 1}. ${prompt.promptTitle}`,
        promptId: prompt.promptId,
      });
    });
  });

  let y = topMargin;

  doc.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
  doc.roundedRect(marginX, y - 6, contentWidth, 78, 8, 8, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(25);
  doc.text(`${detail.project.projectName} Prompt Pack`, marginX + 16, y + 30);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('PromptFab Export', marginX + 16, y + 52);
  y += 96;

  doc.setTextColor(textColor[0], textColor[1], textColor[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const basedOnBook = detail.project.bookTitle?.trim();
  if (basedOnBook) {
    doc.text('Based On', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(basedOnBook, marginX + 78, y);
    y += 18;
  }

  if (detail.project.bookAuthor) {
    doc.setFont('helvetica', 'bold');
    doc.text('Author', marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(detail.project.bookAuthor, marginX + 78, y);
    y += 18;
  }

  doc.setFont('helvetica', 'bold');
  doc.text('Generated', marginX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleString(), marginX + 78, y);
  y += 24;

  const tocStartY = y;
  const firstTocHeadingHeight = 22;
  const continuedHeadingHeight = 22;
  const firstPageRows = Math.max(
    1,
    Math.floor((pageHeight - bottomMargin - (tocStartY + firstTocHeadingHeight)) / lineHeight)
  );
  const nextPageRows = Math.max(
    1,
    Math.floor((pageHeight - bottomMargin - (topMargin + continuedHeadingHeight)) / lineHeight)
  );

  const tocPageNumbers: number[] = [];
  let remainingRows = tocRows.length;
  let pageIndex = 0;

  while (remainingRows > 0 || pageIndex === 0) {
    tocPageNumbers.push(doc.getNumberOfPages());
    remainingRows -= pageIndex === 0 ? firstPageRows : nextPageRows;
    pageIndex += 1;
    if (remainingRows > 0) {
      doc.addPage();
    }
  }

  doc.addPage();
  y = topMargin;

  const promptDestinations = new Map<string, { pageNumber: number; top: number }>();

  const ensureSpace = (neededHeight: number): void => {
    if (y + neededHeight > pageHeight - bottomMargin) {
      doc.addPage();
      y = topMargin;
    }
  };

  const writeWrappedText = (text: string): void => {
    const paragraphs = text.replace(/\r\n/g, '\n').split('\n');
    paragraphs.forEach((paragraph, paragraphIndex) => {
      const lines = doc.splitTextToSize(paragraph || ' ', contentWidth);
      lines.forEach((line: string) => {
        ensureSpace(lineHeight);
        doc.text(line, marginX, y);
        y += lineHeight;
      });

      if (paragraphIndex < paragraphs.length - 1) {
        ensureSpace(6);
        y += 6;
      }
    });
  };

  chapterRows.forEach((chapter, chapterIndex) => {
    if (chapterIndex > 0) {
      doc.addPage();
      y = topMargin;
    }

    doc.setDrawColor(222, 228, 235);
    doc.setFillColor(246, 249, 252);
    ensureSpace(52);
    doc.roundedRect(marginX, y - 2, contentWidth, 34, 6, 6, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.text(`Chapter ${chapter.chapterNumber}: ${chapter.chapterTitle}`, marginX + 12, y + 20);
    y += 44;

    chapter.prompts.forEach((prompt, promptIndex) => {
      ensureSpace(68);

      promptDestinations.set(prompt.promptId, {
        pageNumber: doc.getNumberOfPages(),
        top: Math.max(y - 10, 0),
      });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      const promptLabel = `${promptIndex + 1}. ${prompt.promptTitle}`;
      const titleLines = doc.splitTextToSize(promptLabel, contentWidth);
      titleLines.forEach((line: string) => {
        ensureSpace(lineHeight);
        doc.text(line, marginX, y);
        y += lineHeight;
      });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 92, 148);
      ensureSpace(lineHeight);
      doc.textWithLink('Open this prompt in PromptFab', marginX, y, { url: prompt.deepLink });
      y += lineHeight;

      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      writeWrappedText(prompt.promptText);

      doc.setDrawColor(228, 232, 238);
      ensureSpace(14);
      doc.line(marginX, y + 2, pageWidth - marginX, y + 2);
      y += 16;
    });
  });

  let rowCursor = 0;
  tocPageNumbers.forEach((pageNumber, index) => {
    doc.setPage(pageNumber);

    let tocY = index === 0 ? tocStartY : topMargin;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.text(index === 0 ? 'Table of Contents' : 'Table of Contents (cont.)', marginX, tocY);
    tocY += 22;

    const maxRows = index === 0 ? firstPageRows : nextPageRows;
    for (let i = 0; i < maxRows && rowCursor < tocRows.length; i += 1) {
      const row = tocRows[rowCursor];

      if (row.type === 'chapter') {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        const chapterText = truncateToWidth(row.text, contentWidth);
        doc.text(chapterText, marginX, tocY);
      } else {
        const destination = promptDestinations.get(row.promptId);
        const promptText = truncateToWidth(row.text, contentWidth - 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(mutedColor[0], mutedColor[1], mutedColor[2]);
        doc.text(promptText, marginX + 14, tocY);

        if (destination) {
          const pageLabel = String(destination.pageNumber);
          const pageLabelWidth = doc.getTextWidth(pageLabel);
          const pageLabelX = pageWidth - marginX - pageLabelWidth;
          doc.setTextColor(textColor[0], textColor[1], textColor[2]);
          doc.text(pageLabel, pageLabelX, tocY);

          doc.link(marginX + 14, tocY - lineHeight + 3, contentWidth - 14, lineHeight, {
            pageNumber: destination.pageNumber,
            top: destination.top,
          });
        }
      }

      tocY += lineHeight;
      rowCursor += 1;
    }
  });

  const totalPages = doc.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(mutedColor[0], mutedColor[1], mutedColor[2]);
    const footerY = pageHeight - 28;
    doc.text(`${detail.project.projectName} • PromptFab`, marginX, footerY);
    doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - marginX, footerY, { align: 'right' });
  }

  const safeProjectName = (detail.project.projectName || 'project').replace(/[^a-z0-9-_ ]/gi, '').trim() || 'project';
  doc.save(`${safeProjectName}-prompt-pack.pdf`);
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optimizingAllPrompts, setOptimizingAllPrompts] = useState(false);
  const [optimizeAllDialogOpen, setOptimizeAllDialogOpen] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState<OptimizeProgressState | null>(null);
  const [optimizeActivity, setOptimizeActivity] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importJsonDialogOpen, setImportJsonDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);

  const [name, setName] = useState('');
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [personaMode, setPersonaMode] = useState<'default-from-book' | 'create-new-persona-version'>('default-from-book');
  const [userId, setUserId] = useState<string | null>(null);
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] = useState('');
  const [customPersona, setCustomPersona] = useState('');
  const [personaRoleContext, setPersonaRoleContext] = useState('');

  const isDetail = Boolean(id);

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const sub = await getUserSub();
        if (sub) setUserId(sub);
      } catch {
        // ignore
      }
    };
    void fetchUserId();
  }, []);

  useEffect(() => {
    const fetchPersonas = async () => {
      if (!isDetail || !userId || personaMode !== 'create-new-persona-version') return;

      setPersonasLoading(true);
      try {
        const result = await personaWizApi.listPersonas(userId);
        setPersonas(result.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setPersonasLoading(false);
      }
    };

    void fetchPersonas();
  }, [isDetail, userId, personaMode]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        if (!id) {
          const result = await projectsApi.list();
          setProjects(result.data || []);
          setDetail(null);
        } else {
          const result = await projectsApi.get(id);
          const payload = result.data as ProjectDetail;
          setDetail(payload);
          setName(payload.project.projectName || '');
        }
      } catch (err) {
        console.error(err);
        setError(id ? 'Failed to load project.' : 'Failed to load projects.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [id]);

  const totalPrompts = useMemo(() => {
    if (!detail) return 0;
    return detail.packs.reduce((sum, pack) => sum + pack.prompts.length, 0);
  }, [detail]);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    if (axios.isAxiosError(err)) {
      const message = err.response?.data?.error?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
      const details = err.response?.data?.error?.details;
      const preview = details?.responsePreview;
      if (typeof preview === 'string' && preview.trim()) {
        return `${fallback} ${preview.slice(0, 220)}`;
      }
    }
    return fallback;
  };

  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await projectsApi.update(id, { name });
      setSuccess('Project updated.');
    } catch (err) {
      console.error(err);
      setError('Failed to update project.');
    } finally {
      setSaving(false);
    }
  };

  const reloadDetail = async () => {
    if (!id) return;
    const result = await projectsApi.get(id);
    const payload = result.data as ProjectDetail;
    setDetail(payload);
    setName(payload.project.projectName || '');
  };

  const handleOpenGenerationDialog = () => {
    setError(null);
    setGenerationDialogOpen(true);
  };

  const handleOpenOptimizeAllDialog = () => {
    setOptimizeActivity([]);
    setOptimizeProgress(null);
    setOptimizeAllDialogOpen(true);
  };

  const handleCloseOptimizeAllDialog = () => {
    if (!optimizingAllPrompts) {
      setOptimizeAllDialogOpen(false);
    }
  };

  const handleCloseGenerationDialog = () => {
    if (!generating) {
      setGenerationDialogOpen(false);
    }
  };

  const handleGenerateVersion = async () => {
    if (!id) return;

    if (personaMode === 'create-new-persona-version' && !personaRoleContext.trim()) {
      setError('Persona text is required to clone this project.');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await projectsApi.generateVersion(id, {
        mode: personaMode,
        personaText: personaMode === 'create-new-persona-version' ? personaRoleContext : undefined,
        generationPrompt: generationPrompt.trim() || undefined,
      });

      const packCount = result?.meta?.packCount;
      const generationVersion = result?.meta?.generationVersion;
      const createdProjectId = result?.meta?.createdProjectId as string | undefined;

      if (personaMode === 'create-new-persona-version' && createdProjectId) {
        setSuccess(`Cloned project${typeof packCount === 'number' ? ` with ${packCount} chapter packs` : ''}.`);
        setGenerationDialogOpen(false);
        navigate(`/projects/${createdProjectId}`);
        return;
      }

      setSuccess(
        `Generated new project version${generationVersion ? ` v${generationVersion}` : ''}${
          typeof packCount === 'number' ? ` with ${packCount} chapter packs` : ''
        }.`
      );
      setGenerationDialogOpen(false);

      await reloadDetail();
    } catch (err) {
      console.error(err);
      setError(extractApiErrorMessage(err, 'Failed to clone this project.'));
    } finally {
      setGenerating(false);
    }
  };

  const handleOptimizeAllPrompts = async () => {
    if (!id) return;

    setOptimizingAllPrompts(true);
    setError(null);
    setSuccess(null);
    setOptimizeActivity([]);
    setOptimizeProgress({
      phase: 'staging',
      totalPromptCount: 0,
      stageProcessed: 0,
      commitProcessed: 0,
      clearProcessed: 0,
      stagedCount: 0,
      committedCount: 0,
      clearedCount: 0,
      skippedCount: 0,
      stagedReadyCount: 0,
      unstagedCount: 0,
    });

    const appendActivity = (lines: string[]) => {
      if (lines.length === 0) return;
      setOptimizeActivity((prev) => [...prev, ...lines].slice(-120));
    };

    try {
      let stageCursor: string | undefined;
      let commitCursor: string | undefined;
      let totalPromptCount = 0;
      let totalStageProcessed = 0;
      let totalCommitProcessed = 0;
      let totalClearProcessed = 0;
      let totalStaged = 0;
      let totalCommitted = 0;
      let totalCleared = 0;
      let totalSkipped = 0;
      let stagedReadyCount = 0;
      let unstagedCount = 0;
      const failureReasons: string[] = [];

      let safetyCounter = 0;
      while (safetyCounter < 300) {
        const result = await projectsApi.optimizeAllPrompts(id, {
          action: 'stage',
          overwriteUserPrompt: true,
          cursor: stageCursor,
          limit: 3,
        });

        const meta = (result?.meta || {}) as {
          optimizedCount?: number;
          skippedCount?: number;
          processedCount?: number;
          totalPromptCount?: number;
          stagedReadyCount?: number;
          unstagedCount?: number;
          hasMore?: boolean;
          nextCursor?: string | null;
          failures?: Array<{ reason?: string }>;
          batchResults?: OptimizeBatchResult[];
        };

        totalPromptCount = typeof meta.totalPromptCount === 'number' ? meta.totalPromptCount : totalPromptCount;
        totalStageProcessed += Number(meta.processedCount ?? 0);
        totalStaged += Number(meta.optimizedCount ?? 0);
        totalSkipped += Number(meta.skippedCount ?? 0);
        stagedReadyCount = Number(meta.stagedReadyCount ?? stagedReadyCount);
        unstagedCount = Number(meta.unstagedCount ?? unstagedCount);

        if (Array.isArray(meta.failures)) {
          for (const failure of meta.failures) {
            if (typeof failure?.reason === 'string' && failure.reason.trim()) {
              failureReasons.push(failure.reason);
            }
          }
        }

        if (Array.isArray(meta.batchResults) && meta.batchResults.length > 0) {
          appendActivity(
            meta.batchResults.map((item) => {
              const reason = item.reason ? ` (${item.reason})` : '';
              return `Stage: ${item.promptName} -> ${item.status}${reason}`;
            })
          );
        }

        setOptimizeProgress({
          phase: 'staging',
          totalPromptCount,
          stageProcessed: totalStageProcessed,
          commitProcessed: totalCommitProcessed,
          clearProcessed: totalClearProcessed,
          stagedCount: totalStaged,
          committedCount: totalCommitted,
          clearedCount: totalCleared,
          skippedCount: totalSkipped,
          stagedReadyCount,
          unstagedCount,
        });

        const hasMore = Boolean(meta.hasMore);
        const nextCursor = typeof meta.nextCursor === 'string' && meta.nextCursor.trim() ? meta.nextCursor : undefined;
        if (!hasMore || !nextCursor) {
          break;
        }

        stageCursor = nextCursor;
        safetyCounter += 1;
      }

      if (safetyCounter >= 300) {
        throw new Error('Staging phase exceeded safe batch limit');
      }

      if (unstagedCount > 0) {
        const detail = failureReasons[0] ? ` First failure: ${failureReasons[0]}` : '';
        throw new Error(
          `Optimization staging is incomplete (${unstagedCount} prompts still unstaged). Resume to continue from remaining prompts.${detail}`
        );
      }

      if (stagedReadyCount > 0) {
        setOptimizeProgress((prev) =>
          prev
            ? {
                ...prev,
                phase: 'committing',
              }
            : prev
        );

        safetyCounter = 0;
        while (safetyCounter < 300) {
          const result = await projectsApi.optimizeAllPrompts(id, {
            action: 'commit',
            overwriteUserPrompt: false, // preserve original — optimized text goes to generatedPrompt only
            cursor: commitCursor,
            limit: 6,
          });

          const meta = (result?.meta || {}) as {
            committedCount?: number;
            clearedCount?: number;
            skippedCount?: number;
            processedCount?: number;
            totalPromptCount?: number;
            stagedReadyCount?: number;
            unstagedCount?: number;
            hasMore?: boolean;
            nextCursor?: string | null;
            failures?: Array<{ reason?: string }>;
            batchResults?: OptimizeBatchResult[];
          };

          totalPromptCount = typeof meta.totalPromptCount === 'number' ? meta.totalPromptCount : totalPromptCount;
          totalCommitProcessed += Number(meta.processedCount ?? 0);
          totalCommitted += Number(meta.committedCount ?? 0);
          totalSkipped += Number(meta.skippedCount ?? 0);
          stagedReadyCount = Number(meta.stagedReadyCount ?? stagedReadyCount);
          unstagedCount = Number(meta.unstagedCount ?? unstagedCount);

          if (Array.isArray(meta.failures)) {
            for (const failure of meta.failures) {
              if (typeof failure?.reason === 'string' && failure.reason.trim()) {
                failureReasons.push(failure.reason);
              }
            }
          }

          if (Array.isArray(meta.batchResults) && meta.batchResults.length > 0) {
            appendActivity(
              meta.batchResults.map((item) => {
                const reason = item.reason ? ` (${item.reason})` : '';
                return `Commit: ${item.promptName} -> ${item.status}${reason}`;
              })
            );
          }

          setOptimizeProgress({
            phase: 'committing',
            totalPromptCount,
            stageProcessed: totalStageProcessed,
            commitProcessed: totalCommitProcessed,
            clearProcessed: totalClearProcessed,
            stagedCount: totalStaged,
            committedCount: totalCommitted,
            clearedCount: totalCleared,
            skippedCount: totalSkipped,
            stagedReadyCount,
            unstagedCount,
          });

          const hasMore = Boolean(meta.hasMore);
          const nextCursor = typeof meta.nextCursor === 'string' && meta.nextCursor.trim() ? meta.nextCursor : undefined;
          if (!hasMore || !nextCursor) {
            break;
          }

          commitCursor = nextCursor;
          safetyCounter += 1;
        }

        if (safetyCounter >= 300) {
          throw new Error('Commit phase exceeded safe batch limit');
        }
      }

      if (stagedReadyCount > 0) {
        setOptimizeProgress((prev) =>
          prev
            ? {
                ...prev,
                phase: 'clearing',
              }
            : prev
        );

        let clearCursor: string | undefined;
        safetyCounter = 0;
        while (safetyCounter < 300) {
          const result = await projectsApi.optimizeAllPrompts(id, {
            action: 'clear',
            overwriteUserPrompt: true,
            cursor: clearCursor,
            limit: 10,
          });

          const meta = (result?.meta || {}) as {
            committedCount?: number;
            clearedCount?: number;
            skippedCount?: number;
            processedCount?: number;
            totalPromptCount?: number;
            stagedReadyCount?: number;
            unstagedCount?: number;
            hasMore?: boolean;
            nextCursor?: string | null;
            failures?: Array<{ reason?: string }>;
            batchResults?: OptimizeBatchResult[];
          };

          totalPromptCount = typeof meta.totalPromptCount === 'number' ? meta.totalPromptCount : totalPromptCount;
          totalClearProcessed += Number(meta.processedCount ?? 0);
          totalCleared += Number(meta.clearedCount ?? 0);
          totalSkipped += Number(meta.skippedCount ?? 0);
          stagedReadyCount = Number(meta.stagedReadyCount ?? stagedReadyCount);
          unstagedCount = Number(meta.unstagedCount ?? unstagedCount);

          if (Array.isArray(meta.failures)) {
            for (const failure of meta.failures) {
              if (typeof failure?.reason === 'string' && failure.reason.trim()) {
                failureReasons.push(failure.reason);
              }
            }
          }

          if (Array.isArray(meta.batchResults) && meta.batchResults.length > 0) {
            appendActivity(
              meta.batchResults.map((item) => {
                const reason = item.reason ? ` (${item.reason})` : '';
                return `Clear staged field: ${item.promptName} -> ${item.status}${reason}`;
              })
            );
          }

          setOptimizeProgress({
            phase: 'clearing',
            totalPromptCount,
            stageProcessed: totalStageProcessed,
            commitProcessed: totalCommitProcessed,
            clearProcessed: totalClearProcessed,
            stagedCount: totalStaged,
            committedCount: totalCommitted,
            clearedCount: totalCleared,
            skippedCount: totalSkipped,
            stagedReadyCount,
            unstagedCount,
          });

          const hasMore = Boolean(meta.hasMore);
          const nextCursor = typeof meta.nextCursor === 'string' && meta.nextCursor.trim() ? meta.nextCursor : undefined;
          if (!hasMore || !nextCursor) {
            break;
          }

          clearCursor = nextCursor;
          safetyCounter += 1;
        }

        if (safetyCounter >= 300) {
          throw new Error('Clear phase exceeded safe batch limit');
        }
      }

      if (stagedReadyCount > 0) {
        throw new Error('Optimization was committed but cleanup did not complete. Please run Optimize All again to resume.');
      }

      if (totalCommitted === 0 && totalPromptCount > 0) {
        const detail = failureReasons[0] ? ` First failure: ${failureReasons[0]}` : '';
        throw new Error(`Optimization completed with no committed prompt updates.${detail}`);
      }

      setOptimizeProgress((prev) =>
        prev
          ? {
              ...prev,
              phase: 'done',
              committedCount: totalCommitted,
              clearedCount: totalCleared,
              stagedReadyCount: 0,
              unstagedCount: 0,
            }
          : prev
      );

      setSuccess(
        `Optimized and committed ${totalCommitted}` +
          `${typeof totalPromptCount === 'number' ? ` of ${totalPromptCount}` : ''} prompts` +
          `${totalCleared > 0 ? `; cleared staged field for ${totalCleared}` : ''}` +
          `${totalSkipped > 0 ? ` (${totalSkipped} skipped)` : ''}` +
          `${failureReasons.length > 0 ? ` First failure: ${failureReasons[0]}` : ''}.`
      );

      setOptimizeAllDialogOpen(false);
      await reloadDetail();
    } catch (err) {
      console.error(err);
      setError(extractApiErrorMessage(err, 'Failed to optimize all prompts for this project.'));
    } finally {
      setOptimizingAllPrompts(false);
    }
  };

  const openOptimizer = (prompt: ProjectPrompt) => {
    navigate(`/optimizer?promptId=${encodeURIComponent(prompt.entityId)}&projectId=${encodeURIComponent(id || '')}`);
  };

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setDeleteTarget({ id: projectId, name: projectName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await projectsApi.delete(deleteTarget.id);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      if (isDetail) {
        navigate('/projects', { replace: true });
      } else {
        setProjects((prev) => prev.filter((p) => p.entityId !== deleteTarget.id));
      }
    } catch (err) {
      console.error(err);
      setError('Failed to delete project.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleExportPromptPackPdf = async () => {
    if (!detail) return;

    setExportingPdf(true);
    setError(null);
    setSuccess(null);

    try {
      exportProjectPromptPackPdf(detail);
      setSuccess('Prompt pack PDF exported.');
    } catch (err) {
      console.error(err);
      setError('Failed to export prompt pack PDF.');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await projectsApi.create({
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
      });
      const newId = (result as { data: { entityId: string } }).data.entityId;
      setCreateDialogOpen(false);
      setNewProjectName('');
      setNewProjectDescription('');
      navigate(`/projects/${newId}`);
    } catch (err) {
      console.error(err);
      setError(extractApiErrorMessage(err, 'Failed to create project.'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!isDetail && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Projects</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportJsonDialogOpen(true)}>
              Import from JSON
            </Button>
            <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
              New Project
            </Button>
          </Box>
        </Box>
      )}

      {isDetail && (
        <Typography
          variant="body2"
          color="primary"
          sx={{ mb: 2, cursor: 'pointer', width: 'fit-content' }}
          onClick={() => navigate('/projects')}
        >
          Back
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!isDetail && (
        <Grid container spacing={2}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.entityId}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {project.projectName}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {project.description || 'No description'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Book: {project.bookId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Packs: {project.packCount}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Prompts: {project.promptCount}
                  </Typography>
                  <Button variant="contained" onClick={() => navigate(`/projects/${project.entityId}`)}>
                    Open Project
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => handleDeleteClick(project.entityId, project.projectName)}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {projects.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary">No projects yet.</Typography>
            </Grid>
          )}
        </Grid>
      )}

      {isDetail && detail && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6">Project Settings</Typography>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    color="primary"
                    aria-label="Save Project Settings"
                    title="Save Project Settings"
                    onClick={() => void handleSave()}
                    disabled={saving || !name.trim()}
                  >
                    {saving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                  </IconButton>
                  <Button
                    variant="outlined"
                    onClick={handleOpenOptimizeAllDialog}
                    disabled={optimizingAllPrompts || !detail.packs.length}
                  >
                    Optimize Prompts
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => void handleExportPromptPackPdf()}
                    disabled={exportingPdf || !detail.packs.length}
                  >
                    {exportingPdf ? 'Downloading PDF...' : 'Download PDF'}
                  </Button>
                  <Button variant="outlined" onClick={handleOpenGenerationDialog} disabled={generating}>
                    Clone Project
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PublicIcon />}
                    onClick={() => setPublishDialogOpen(true)}
                    disabled={!totalPrompts}
                  >
                    Publish to Library
                  </Button>
                </Stack>
              </Box>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <TextField
                    label="Project Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    <b>Packs: {detail.packs.length} | Prompts: {totalPrompts}</b> || Book ID: {detail.project.bookId || 'N/A'} 
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Chapter Packs
              </Typography>

              <Stack spacing={2}>
                {detail.packs.map((pack) => (
                  <Box key={pack.entityId}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {pack.chapterIndex ? `Chapter ${pack.chapterIndex}: ` : ''}
                      {pack.chapterTitle || pack.promptPackName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {pack.promptPackName}
                    </Typography>

                    {pack.prompts.length > 0 ? (
                      <Grid container spacing={1.5}>
                        {pack.prompts.map((prompt) => (
                          <Grid item xs={12} sm={6} xl={4} key={prompt.entityId}>
                            <Card
                              variant="outlined"
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  height: '100%',
                                }}
                              >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between',
                                      gap: 1,
                                      mb: 0.75,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 500,
                                        minWidth: 0,
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      noWrap
                                      onClick={() => openOptimizer(prompt)}
                                    >
                                      {/* Strip leading "ChapterTitle: " prefix if present (legacy data) */}
                                      {pack.chapterTitle && prompt.promptName.startsWith(`${pack.chapterTitle}: `)
                                        ? prompt.promptName.slice(pack.chapterTitle.length + 2)
                                        : prompt.promptName}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexShrink: 0, mt: -0.25 }}>
                                      <IconButton
                                        size="small"
                                        aria-label={`Copy link to ${prompt.promptName}`}
                                        onClick={() => {
                                          const url = `${window.location.origin}/optimizer?promptId=${encodeURIComponent(prompt.entityId)}`;
                                          void navigator.clipboard.writeText(url);
                                        }}
                                      >
                                        <LinkIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label={`Edit ${prompt.promptName}`}
                                        onClick={() => openOptimizer(prompt)}
                                      >
                                        <EditOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      overflow: 'hidden',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {prompt.description || prompt.userPrompt || prompt.generatedPrompt || ''}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No prompts in this pack.
                      </Typography>
                    )}

                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
                {detail.packs.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No chapter packs found for this project.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ borderColor: 'error.main', border: '1px solid' }}>
            <CardContent>
              <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete this project. The chapter prompt packs will remain in your
                Packs library but will no longer be associated with a project.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  handleDeleteClick(
                    detail.project.entityId,
                    detail.project.projectName
                  )
                }
              >
                Delete Project
              </Button>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Project?"
        message={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={handleDeleteCancel}
      />

      {/* Generation dialog moved from inline section */}
      <Dialog open={generationDialogOpen} onClose={handleCloseGenerationDialog} maxWidth="md" fullWidth>
        <DialogTitle>Clone Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Persona</InputLabel>
              <Select
                value={personaMode}
                label="Select Persona"
                onChange={(e) => {
                  const value = e.target.value as 'default-from-book' | 'create-new-persona-version';
                  setPersonaMode(value);
                  if (value === 'default-from-book') {
                    setSelectedPersonaId('');
                    setCustomPersona('');
                    setPersonaRoleContext('');
                  }
                }}
              >
                <MenuItem value="default-from-book">Default from Book</MenuItem>
                <MenuItem value="create-new-persona-version">Create New Persona Version</MenuItem>
              </Select>
            </FormControl>

            {personaMode === 'create-new-persona-version' && (
              <PersonaSelector
                personas={personas}
                personasLoading={personasLoading}
                selectedPersonaId={selectedPersonaId}
                customPersona={customPersona}
                personaRoleContext={personaRoleContext}
                onSelectedPersonaIdChange={setSelectedPersonaId}
                onCustomPersonaChange={setCustomPersona}
                onPersonaRoleContextChange={setPersonaRoleContext}
                title="Persona Selector"
                helperText="Select a persona from PersonaFab.com or enter a freeform persona description."
              />
            )}

            <TextField
              label="Generation Prompt"
              fullWidth
              multiline
              rows={3}
              value={generationPrompt}
              onChange={(e) => setGenerationPrompt(e.target.value)}
              placeholder="Override the default authored-books chapter generation prompt"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenerationDialog} disabled={generating}>
            Cancel
          </Button>
          <Button onClick={() => void handleGenerateVersion()} variant="contained" disabled={generating}>
            {generating ? 'Cloning...' : 'Clone Project'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={optimizeAllDialogOpen} onClose={handleCloseOptimizeAllDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Optimize All Prompts?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography>
              This will generate an AI-optimized version of every prompt and save it alongside the original. Your original prompts are preserved — the optimized versions are stored separately and can be viewed in the individual prompt optimizer.
            </Typography>

            {optimizeProgress ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Phase: <b>{optimizeProgress.phase}</b> | Total: <b>{optimizeProgress.totalPromptCount}</b> | Staged:{' '}
                  <b>{optimizeProgress.stagedCount}</b> | Committed: <b>{optimizeProgress.committedCount}</b> | Cleared:{' '}
                  <b>{optimizeProgress.clearedCount}</b> | Staged ready: <b>{optimizeProgress.stagedReadyCount}</b> | Unstaged remaining:{' '}
                  <b>{optimizeProgress.unstagedCount}</b>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    optimizeProgress.totalPromptCount > 0
                      ? Math.min(
                          100,
                          Math.round(
                            ((optimizeProgress.phase === 'staging'
                              ? optimizeProgress.stageProcessed / optimizeProgress.totalPromptCount / 3
                              : optimizeProgress.phase === 'committing'
                                ? 1 / 3 + optimizeProgress.commitProcessed / optimizeProgress.totalPromptCount / 3
                                : optimizeProgress.phase === 'clearing'
                                  ? 2 / 3 + optimizeProgress.clearProcessed / optimizeProgress.totalPromptCount / 3
                                  : 1) || 0) * 100
                          )
                        )
                      : 0
                  }
                />

                <List dense sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {optimizeActivity.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No batch activity yet." />
                    </ListItem>
                  ) : (
                    optimizeActivity.slice().reverse().map((line, index) => (
                      <ListItem key={`${index}-${line}`} sx={{ py: 0.25 }}>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={line} />
                      </ListItem>
                    ))
                  )}
                </List>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOptimizeAllDialog} disabled={optimizingAllPrompts}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleOptimizeAllPrompts()}
            disabled={optimizingAllPrompts}
            startIcon={optimizingAllPrompts ? <CircularProgress size={16} /> : undefined}
          >
            {optimizingAllPrompts ? 'Optimizing...' : 'Optimize All Prompts'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create project dialog */}
      <Dialog open={createDialogOpen} onClose={() => { if (!creating) { setCreateDialogOpen(false); setNewProjectName(''); setNewProjectDescription(''); } }} maxWidth="xs" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Project Name"
              fullWidth
              autoFocus
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              inputProps={{ maxLength: 200 }}
              disabled={creating}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              disabled={creating}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); setNewProjectName(''); setNewProjectDescription(''); }} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateProject()}
            disabled={creating || !newProjectName.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : undefined}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportProjectDialog
        open={importJsonDialogOpen}
        onClose={() => setImportJsonDialogOpen(false)}
        importFn={projectsApi.importJson}
        onImported={({ projectId }) => {
          setImportJsonDialogOpen(false);
          navigate(`/projects/${projectId}`);
        }}
      />

      {detail && (
        <PublishToLibraryDialog
          open={publishDialogOpen}
          onClose={() => setPublishDialogOpen(false)}
          type="project"
          sourceId={detail.project.entityId}
          defaultTitle={detail.project.projectName || 'Untitled Project'}
          defaultDescription={detail.project.description}
          onPublished={(res) => {
            const n = res.itemsPublished ?? res.items?.length ?? 0;
            setSuccess(
              n > 0
                ? `Published ${n} prompt${n === 1 ? '' : 's'} to the public Library — one entry per prompt, grouped by project and chapter.`
                : 'Project published to the public Library.',
            );
          }}
        />
      )}
    </Box>
  );
}
